import { Alert, Button, Card, DatePicker, Form, Select, Space, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { downloadFile } from '@/shared/lib/download';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  classSectionId: number;
  month: Dayjs;
}

export function FeeVouchersPage() {
  const [params, setParams] = useSearchParams();
  const yearId = useAuthStore((s) => s.academicYearId);

  const [form] = Form.useForm<FormShape>();

  const classSectionsQuery = useQuery({
    queryKey: ['setup', 'class-sections', yearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(yearId ?? undefined),
    enabled: yearId != null,
    staleTime: 5 * 60 * 1000,
  });
  const classSections = (classSectionsQuery.data ?? [])
    .slice()
    .sort(
      (a, b) =>
        a.classSortOrder - b.classSortOrder ||
        a.sectionName.localeCompare(b.sectionName),
    );

  // Prefill from URL query (so /fee/vouchers?classSectionId=11&month=2026-05 works).
  useEffect(() => {
    const csParam = params.get('classSectionId');
    const monthParam = params.get('month');
    form.setFieldsValue({
      classSectionId: csParam ? Number(csParam) : undefined,
      month: monthParam ? dayjs(monthParam + '-01') : dayjs(),
    } as Partial<FormShape>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onFinish = async (values: FormShape) => {
    const month = values.month.format('YYYY-MM');
    try {
      await downloadFile(
        '/reports/exports/fee-vouchers.pdf',
        { classSectionId: values.classSectionId, month },
        `fee-vouchers-cs${values.classSectionId}-${month}.pdf`,
      );
      // Keep URL in sync so this is shareable / reload-friendly.
      const next = new URLSearchParams(params);
      next.set('classSectionId', String(values.classSectionId));
      next.set('month', month);
      setParams(next, { replace: true });
    } catch (e) {
      // Surfaces backend's "No students owe fees for this month" error cleanly.
      // eslint-disable-next-line no-alert
      alert(extractError(e, 'Voucher download failed'));
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Fee"
        title="Print fee vouchers"
        description="Bulk-print the Pakistan-style 3-copy challan (Bank / School / Student) — one A4 page per student with outstanding dues for the selected month."
      />

      <Card style={{ maxWidth: 720 }}>
        <Form<FormShape>
          form={form}
          layout="vertical"
          initialValues={{ month: dayjs() }}
          onFinish={onFinish}
        >
          <Form.Item
            label="Class · Section"
            name="classSectionId"
            rules={[{ required: true, message: 'Pick a class · section' }]}
          >
            <Select
              placeholder="Pick a class"
              loading={classSectionsQuery.isLoading}
              showSearch
              optionFilterProp="label"
              options={classSections.map((cs) => ({
                value: cs.id,
                label: `${cs.className} · ${cs.sectionName}`,
              }))}
              style={{ maxWidth: 360 }}
            />
          </Form.Item>

          <Form.Item
            label="Month"
            name="month"
            rules={[{ required: true, message: 'Pick a month' }]}
            help="Invoices for this period plus any one-time dues are bundled into each student's voucher."
          >
            <DatePicker.MonthPicker
              format="MMM YYYY"
              allowClear={false}
              style={{ maxWidth: 220 }}
            />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" icon={<DownloadOutlined />}>
              Download PDF
            </Button>
          </Space>
        </Form>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 20 }}
          message="What gets printed"
          description={
            <Typography.Text>
              One A4 page per active student in the chosen class · section, with three stacked copies
              (Bank · School · Student) per page. Students with zero outstanding for the selected
              month are silently skipped, so the output matches who actually owes money. Due date is
              set to the 10th of the period month.
            </Typography.Text>
          }
        />
      </Card>
    </div>
  );
}
